import type { RefObject } from 'react';
import type { Maybe } from 'yup';

import type {
  ConfigurationActivationConfig,
  ConfigurationGetCheckoutConfigsQuery,
  ConfigurationGetModuleActivationsQuery,
  ConfigurationModule,
} from '@/graphql/configuration/api';
import type {
  AddressValidationRulesQuery,
  CountryCode,
} from '@/graphql/saleor/api';
import { createSafeContext } from '@/lib/context';
import type { EcsConfig } from '@/lib/service/type';

export type ValidationRules = NonNullable<
  AddressValidationRulesQuery['addressValidationRules']
>;

export type GetValidationRules = (
  country: CountryCode,
) => Promise<ValidationRules | null>;

export type GetWarrantyExtensionCampaignStatus = (
  country: CountryCode,
) => Promise<boolean>;

export type GetModuleActivations = (
  country: CountryCode,
  module: ConfigurationModule[],
  refresh: boolean,
) => Promise<ConfigurationGetModuleActivationsQuery>;

export type GetCheckoutConfigs = (
  country: CountryCode,
) => Promise<ConfigurationGetCheckoutConfigsQuery>;

export type ConfigProviderContext = {
  affirmPaymentMax?: number;
  agosPaymentMax: number;
  agosPaymentMin: number;
  applepayExpressPaymentMax?: number | null;
  applepayExpressPaymentMin?: number | null;
  cembraFinancingPaymentMax: number;
  cembraFinancingPaymentMin: number;
  cembraOpenInvoicePaymentMax: number;
  cembraOpenInvoicePaymentMin: number;
  creditPlusPaymentMax: number;
  creditPlusPaymentMin: number;
  ecsConfig: Maybe<EcsConfig>;
  ecsConfigurations: EcsConfig[];
  getCheckoutConfigs: GetCheckoutConfigs;
  getModuleActivations: GetModuleActivations;
  getValidationRules: GetValidationRules;
  getWarrantyExtensionCampaignStatus: GetWarrantyExtensionCampaignStatus;
  headerRef: RefObject<HTMLElement> | null;
  klarnaPayNowPaymentMax?: number;
  moduleActivations: ConfigurationActivationConfig[] | null;
  moduleActivationsCountry?: CountryCode | null;
  moduleActivationsStatus?: 'loading' | 'resolved' | 'error';
  oney3xPaymentMax: number;
  oney3xPaymentMin: number;
  oney4xPaymentMax: number;
  oney4xPaymentMin: number;
  paypalExpressPaymentMax?: number | null;
  paypalExpressPaymentMin?: number | null;
  restoreCheckoutInitialized: boolean;
  setHeaderRef(ref: RefObject<HTMLElement> | null): void;
  smbcPaymentMax?: number | null;
  smbcPaymentMin?: number | null;
  twintPaymentMax?: number;
  v12PaymentMax?: number;
  v12PaymentMin?: number;
  warrantyExtensionCampaignEnable: boolean;
  warrantyExtensionCampaignEndDate: string;
  warrantyExtensionCampaignStartDate: string;
  yourAssetPaymentMax?: number;
  yourAssetPaymentMin?: number;
  zipPaymentMax?: number;
  zipPaymentMin?: number;
};

export const { Provider, useContext: useConfigProvider } =
  createSafeContext<ConfigProviderContext>(
    'useConfigProvider must be used inside a ConfigProvider',
  );
